import React, { useState } from "react";
import { UserProfile } from "../types";
import { saveUserProfile } from "../utils/storage";

interface UserCaptureFormProps {
  isOpen: boolean;
  onComplete: (profile: UserProfile) => void;
  onCancel: () => void;
}

const UserCaptureForm: React.FC<UserCaptureFormProps> = ({
  isOpen,
  onComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('UserCaptureForm handleSubmit called');
    console.log('Form data:', formData);

    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      console.log('Missing required fields');
      return; // Don't submit if required fields are empty
    }

    // Create a basic profile with the captured information
    const newProfile: UserProfile = {
      id: Date.now().toString(36) + Math.random().toString(36).substr(2),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      handicap: 20, // Default casual player handicap - will be updated during onboarding
      height: 70,
      weight: 180,
      age: 35,
      swingSpeed: undefined,
      swingSpeedRange: undefined,
      swingTempo: "medium",
      ballFlight: "straight",
      skillLevel: "intermediate",
      playingExperience: "casual",
      courseFrequency: "monthly",
      missPattern: undefined,
      preferredCourseType: undefined,
      golfGoals: [],
      currentIssues: [],
      clubDistances: [],
      golfBag: [],
      preferredUnits: "yards",
      hasCompletedOnboarding: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    saveUserProfile(newProfile);
    onComplete(newProfile);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: "2rem",
          padding: "2rem",
          maxWidth: "500px",
          width: "100%",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div
            style={{
              width: "4rem",
              height: "4rem",
              margin: "0 auto 1rem",
              background: "linear-gradient(135deg, var(--primary-500), var(--primary-600))",
              borderRadius: "1.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 10px 30px rgba(34, 197, 94, 0.3)",
            }}
          >
            <span style={{ color: "white", fontWeight: "bold", fontSize: "1.5rem" }}>
              CQ
            </span>
          </div>
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "800",
              color: "var(--gray-900)",
              marginBottom: "0.5rem",
            }}
          >
            Welcome to ClubIQ! 🎯
          </h2>
          <p style={{ color: "var(--gray-600)", fontSize: "0.875rem" }}>
            To provide you with personalized golf advice, please share some basic information.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "var(--gray-700)",
                marginBottom: "0.75rem",
              }}
            >
              First Name *
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              style={{
                width: "100%",
                padding: "1rem",
                border: "2px solid var(--gray-200)",
                borderRadius: "1rem",
                fontSize: "1rem",
                background: "rgba(255, 255, 255, 0.9)",
                transition: "all 0.2s",
              }}
              placeholder="Enter your first name"
              required
            />
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "var(--gray-700)",
                marginBottom: "0.75rem",
              }}
            >
              Last Name *
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              style={{
                width: "100%",
                padding: "1rem",
                border: "2px solid var(--gray-200)",
                borderRadius: "1rem",
                fontSize: "1rem",
                background: "rgba(255, 255, 255, 0.9)",
                transition: "all 0.2s",
              }}
              placeholder="Enter your last name"
              required
            />
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <label
              style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "var(--gray-700)",
                marginBottom: "0.75rem",
              }}
            >
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              style={{
                width: "100%",
                padding: "1rem",
                border: "2px solid var(--gray-200)",
                borderRadius: "1rem",
                fontSize: "1rem",
                background: "rgba(255, 255, 255, 0.9)",
                transition: "all 0.2s",
              }}
              placeholder="Enter your email address"
              required
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: "1rem 2rem",
                background: "rgba(255, 255, 255, 0.9)",
                color: "var(--gray-700)",
                border: "2px solid var(--gray-200)",
                borderRadius: "1rem",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              Skip for Now
            </button>
            <button
              type="submit"
              style={{
                padding: "1rem 2rem",
                background: "linear-gradient(135deg, var(--primary-500), var(--primary-600))",
                color: "white",
                border: "none",
                borderRadius: "1rem",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: "0 10px 30px rgba(34, 197, 94, 0.3)",
              }}
            >
              Get Started
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserCaptureForm;
